import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { formatAmount } from '@goaltree/backend/convex/lib/habitMeasure'
import {
  type ItemKind,
  type ItemLog,
  MAX_CUE,
  MAX_ITEMS,
  MAX_NAME,
  MAX_SESSIONS,
  type PlanItem,
  type PlanProfile,
  type PlanSession,
  type Progression,
  newId,
} from '@goaltree/backend/convex/lib/sessionPlan'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import type { TFunction } from 'i18next'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Pencil,
  Play,
  Plus,
  Route,
  Sparkles,
  Square,
  Timer,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useLiveHabit } from '@/components/habit-stack'
import { TimerRing, clock, useNow } from '@/components/number-habit'
import { haptic } from '@/lib/haptics'
import { playBellSound } from '@/lib/sounds'
import { handleUsageLimit } from '@/lib/usage-limit'

// Workout session plans on a habit: build or ask the coach for one, then run
// today's session as a checklist. Finishing ticks the habit via `onComplete`
// (the caller's normal completion path).

type PlanView = NonNullable<FunctionReturnType<typeof api.sessionPlans.getPlan>>
type SessionDoc = NonNullable<PlanView['session']>

const KIND_ICON = { sets: Dumbbell, timed: Timer, distance: Route } as const

export function KindIcon({ kind, className }: { kind: ItemKind; className?: string }) {
  const Icon = KIND_ICON[kind]
  return (
    <Icon className={cn('size-4 shrink-0 text-muted-foreground', className)} strokeWidth={2.2} />
  )
}

const num = (n: number | undefined, lang: string) => formatAmount(n ?? 0, lang)

function duration(t: TFunction, s: number, lang: string) {
  return s >= 60 && s % 60 === 0
    ? t('sessionPlan.min', { n: num(s / 60, lang) })
    : t('sessionPlan.sec', { n: num(s, lang) })
}

export function targetLine(t: TFunction, i: PlanItem, lang: string) {
  const sets = i.sets > 1 ? `${i.sets} × ` : ''
  if (i.kind === 'sets')
    return `${i.sets} × ${num(i.reps, lang)}${i.weight ? ` @ ${num(i.weight, lang)} ${i.unit ?? 'kg'}` : ''}`
  if (i.kind === 'timed') return `${sets}${duration(t, i.seconds ?? 0, lang)}`
  return `${sets}${num(i.distance, lang)} ${i.unit ?? 'km'}`
}

function progressionAmount(t: TFunction, p: Progression, n: number, lang: string) {
  if (p.field === 'seconds') return duration(t, n, lang)
  if (p.field === 'reps') return t('sessionPlan.repsN', { count: n })
  return `${num(n, lang)} ${p.unit ?? ''}`.trim()
}

export function planMetaText(t: TFunction, s: { name: string; items: number }) {
  const count = t('sessionPlan.exercises', { count: s.items })
  return s.name ? `${s.name} · ${count}` : count
}

// "Push day · 5 exercises" under a planned habit's title.
export function HabitPlanLine({ id, className }: { id: Id<'nodes'>; className?: string }) {
  const { t } = useTranslation()
  const s = useLiveHabit(id)?.sessionPlan
  if (!s) return null
  return (
    <p className={cn('flex min-w-0 items-center gap-1 text-xs text-muted-foreground', className)}>
      <Dumbbell className="size-3 shrink-0" strokeWidth={2.2} />
      <span className="truncate">{planMetaText(t, s)}</span>
    </p>
  )
}

const sectionLabel = 'text-[10px] font-semibold uppercase tracking-widest text-muted-foreground'
const btn =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'
const primaryBtn = cn(btn, 'bg-primary text-primary-foreground hover:bg-primary/90')
const ghostBtn = cn(btn, 'text-muted-foreground hover:bg-muted hover:text-foreground')
const outlineBtn = cn(btn, 'border border-border/70 text-foreground hover:bg-muted')
const input =
  'h-8 min-w-0 rounded-md border border-border/70 bg-background px-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50'

type Mode = 'view' | 'choose' | 'ask' | 'edit'

export function SessionPlanSection({
  habitId,
  doneToday,
  onComplete,
  coachName,
}: {
  habitId: Id<'nodes'>
  doneToday: boolean
  onComplete: (id: Id<'nodes'>) => void
  coachName: string
}) {
  const { t } = useTranslation()
  const data = useQuery(api.sessionPlans.getPlan, { habitId })
  const [mode, setMode] = useState<Mode>('view')
  const [draft, setDraft] = useState<PlanSession[]>([])
  const [profile, setProfile] = useState<PlanProfile | undefined>()
  const hasPlan = !!data?.plan

  useEffect(() => {
    setMode('view')
  }, [habitId])

  if (!data) return null

  const openEditor = (sessions: PlanSession[], p?: PlanProfile) => {
    setDraft(sessions)
    setProfile(p)
    setMode('edit')
  }

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="session-plan">
      <span className={sectionLabel}>{t('sessionPlan.section')}</span>
      {mode === 'edit' ? (
        <PlanEditor
          habitId={habitId}
          initial={draft}
          profile={profile}
          canRemove={hasPlan}
          onDone={() => setMode('view')}
        />
      ) : mode === 'ask' ? (
        <AskCoachForm
          habitId={habitId}
          coachName={coachName}
          initial={data.plan?.profile ?? undefined}
          onCancel={() => setMode(hasPlan ? 'view' : 'choose')}
          onPlanned={openEditor}
        />
      ) : !data.plan ? (
        mode === 'choose' ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={primaryBtn} onClick={() => setMode('ask')}>
              <Sparkles className="size-3.5" />
              {t('sessionPlan.askCoach', { coach: coachName })}
            </button>
            <button
              type="button"
              className={outlineBtn}
              onClick={() => openEditor([blankSession(t)])}
            >
              <Pencil className="size-3.5" />
              {t('sessionPlan.buildMyself')}
            </button>
            <button type="button" className={ghostBtn} onClick={() => setMode('view')}>
              {t('sessionPlan.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMode('choose')}
            className="flex w-fit cursor-pointer items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" />
            {t('sessionPlan.add')}
          </button>
        )
      ) : data.session?.status === 'active' ? (
        <SessionRunner
          habitId={habitId}
          view={data}
          session={data.session}
          doneToday={doneToday}
          onComplete={onComplete}
        />
      ) : (
        <PlanOverview
          habitId={habitId}
          view={data}
          onEdit={() => openEditor(data.plan!.sessions, data.plan!.profile ?? undefined)}
          onReplan={() => setMode('ask')}
          coachName={coachName}
        />
      )}
      {(data.plan || mode === 'edit') && (
        <p className="text-[11px] leading-snug text-muted-foreground/80">
          {t('sessionPlan.safety')}
        </p>
      )}
    </div>
  )
}

const blankItem = (): PlanItem => ({ id: newId(), name: '', kind: 'sets', sets: 3, reps: 10 })
const blankSession = (t: TFunction, n = 0): PlanSession => ({
  id: newId(),
  name: n ? t('sessionPlan.sessionN', { n: n + 1 }) : '',
  items: [blankItem()],
})

function PlanOverview({
  habitId,
  view,
  onEdit,
  onReplan,
  coachName,
}: {
  habitId: Id<'nodes'>
  view: PlanView
  onEdit: () => void
  onReplan: () => void
  coachName: string
}) {
  const { t, i18n } = useTranslation()
  const plan = view.plan!
  const start = useMutation(api.sessionPlans.startSession)
  const [shown, setShown] = useState(plan.next)
  const doneToday = view.session?.status === 'done'
  const s = plan.sessions[Math.min(shown, plan.sessions.length - 1)]
  const begin = (sessionId?: string) =>
    void start({ habitId, sessionId }).catch(() => toast.error(t('sessionPlan.error')))

  return (
    <div className="flex flex-col gap-2">
      {plan.sessions.length > 1 && (
        <div className="flex flex-wrap gap-1" role="tablist">
          {plan.sessions.map((x, i) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              aria-selected={i === shown}
              onClick={() => setShown(i)}
              className={cn(
                'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
                i === shown
                  ? 'bg-foreground text-background'
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {x.name || t('sessionPlan.sessionN', { n: i + 1 })}
              {i === plan.next && !doneToday && (
                <span className="ml-1 font-normal opacity-70">· {t('sessionPlan.next')}</span>
              )}
            </button>
          ))}
        </div>
      )}
      <ul className="flex flex-col divide-y divide-border/50 rounded-xl border border-border/60">
        {s.items.map((i) => (
          <li key={i.id} className="flex items-start gap-2.5 px-3 py-2">
            <KindIcon kind={i.kind} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-foreground">
                {i.name}
                <span className="font-mono text-xs font-normal text-muted-foreground">
                  {targetLine(t, i, i18n.language)}
                </span>
              </p>
              {i.cue && <p className="text-xs text-muted-foreground">{i.cue}</p>}
            </div>
          </li>
        ))}
      </ul>
      {view.last && (
        <p className="text-xs text-muted-foreground">
          {t('sessionPlan.lastSession', {
            name: view.last.sessionName || t('sessionPlan.section'),
            done: view.last.done,
            total: view.last.total,
          })}
          {view.last.prs.length > 0 &&
            ` · ${t('sessionPlan.prs', { list: view.last.prs.join(', ') })}`}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {doneToday ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text">
            <Check className="size-3.5" />
            {t('sessionPlan.doneToday', { name: view.session?.sessionName ?? '' })}
          </span>
        ) : (
          <button
            type="button"
            className={primaryBtn}
            onClick={() => begin(shown === plan.next ? undefined : s.id)}
          >
            <Play className="size-3.5" />
            {plan.sessions.length > 1 && s.name
              ? t('sessionPlan.startNamed', { name: s.name })
              : t('sessionPlan.start')}
          </button>
        )}
        <button type="button" className={ghostBtn} onClick={onEdit}>
          <Pencil className="size-3.5" />
          {t('sessionPlan.edit')}
        </button>
        <button type="button" className={ghostBtn} onClick={onReplan}>
          <Sparkles className="size-3.5" />
          {t('sessionPlan.replan', { coach: coachName })}
        </button>
      </div>
    </div>
  )
}

function ProgressionChips({ habitId, items }: { habitId: Id<'nodes'>; items: Progression[] }) {
  const { t, i18n } = useTranslation()
  const accept = useMutation(api.sessionPlans.acceptProgression)
  const dismiss = useMutation(api.sessionPlans.dismissProgression)
  if (!items.length) return null
  const args = (p: Progression) => ({
    habitId,
    itemId: p.itemId,
    field: p.field,
    from: p.from,
    to: p.to,
  })
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((p) => (
        <div
          key={`${p.itemId}:${p.field}`}
          className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2"
        >
          <p className="flex gap-2 text-xs leading-snug text-foreground">
            <TrendingUp className="mt-px size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
            {t('sessionPlan.progressLine', {
              name: p.name,
              from: progressionAmount(t, p, p.from, i18n.language),
              to: progressionAmount(t, p, p.to, i18n.language),
            })}
          </p>
          <div className="mt-1.5 flex gap-1">
            <button
              type="button"
              className={cn(primaryBtn, 'py-1')}
              onClick={() => {
                haptic('success')
                void accept(args(p)).catch(() => toast.error(t('sessionPlan.error')))
              }}
            >
              {t('sessionPlan.accept', { amount: progressionAmount(t, p, p.to, i18n.language) })}
            </button>
            <button
              type="button"
              className={cn(ghostBtn, 'py-1')}
              onClick={() => void dismiss(args(p)).catch(() => toast.error(t('sessionPlan.error')))}
            >
              {t('sessionPlan.notNow')}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

type SetPatch = {
  done?: boolean
  reps?: number
  weight?: number
  seconds?: number
  distance?: number
}

function SessionRunner({
  habitId,
  view,
  session,
  doneToday,
  onComplete,
}: {
  habitId: Id<'nodes'>
  view: PlanView
  session: SessionDoc
  doneToday: boolean
  onComplete: (id: Id<'nodes'>) => void
}) {
  const { t } = useTranslation()
  const plan = view.plan!
  const start = useMutation(api.sessionPlans.startSession)
  const update = useMutation(api.sessionPlans.updateSet).withOptimisticUpdate((store, args) => {
    const cur = store.getQuery(api.sessionPlans.getPlan, { habitId })
    if (!cur?.session) return
    const { sessionId: _s, itemId, set, ...patch } = args
    const clean = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined))
    store.setQuery(
      api.sessionPlans.getPlan,
      { habitId },
      {
        ...cur,
        session: {
          ...cur.session,
          items: cur.session.items.map((l) =>
            l.itemId !== itemId
              ? l
              : { ...l, sets: l.sets.map((x, i) => (i === set ? { ...x, ...clean } : x)) },
          ),
        },
      },
    )
  })
  const finishM = useMutation(api.sessionPlans.finishSession)
  const finishing = useRef(false)
  const [timer, setTimer] = useState<{ itemId: string; set: number; endsAt: number } | null>(null)

  const untouched = session.items.every((l) => l.sets.every((s) => !s.done))
  const remaining = session.items.filter((l) => l.sets.some((s) => !s.done)).length

  const finish = () => {
    if (finishing.current) return
    finishing.current = true
    setTimer(null)
    finishM({ sessionId: session._id })
      .then((r) => {
        if (r.prs.length) toast.success(t('sessionPlan.prs', { list: r.prs.join(', ') }))
        if (!doneToday) onComplete(habitId)
      })
      .catch(() => toast.error(t('sessionPlan.error')))
      .finally(() => {
        finishing.current = false
      })
  }

  const patchSet = (l: ItemLog, set: number, patch: SetPatch) => {
    void update({ sessionId: session._id, itemId: l.itemId, set, ...patch }).catch(() =>
      toast.error(t('sessionPlan.error')),
    )
    if (patch.done) {
      haptic('light')
      const allDone = session.items.every((x) =>
        x.sets.every((s, i) => s.done || (x.itemId === l.itemId && i === set)),
      )
      if (allDone) finish()
    }
  }

  return (
    <div className="flex flex-col gap-2.5" data-testid="session-runner">
      <div className="flex flex-wrap items-center gap-1.5">
        {plan.sessions.length > 1 && untouched ? (
          plan.sessions.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => void start({ habitId, sessionId: x.id })}
              className={cn(
                'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
                x.id === session.sessionId
                  ? 'bg-foreground text-background'
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {x.name}
            </button>
          ))
        ) : (
          <span className="text-sm font-semibold text-foreground">
            {session.sessionName || t('sessionPlan.today')}
          </span>
        )}
      </div>
      <ProgressionChips habitId={habitId} items={view.progressions} />
      <ul className="flex flex-col gap-2">
        {session.items.map((l) => {
          const item = plan.sessions
            .find((s) => s.id === session.sessionId)
            ?.items.find((i) => i.id === l.itemId)
          return (
            <ItemRow
              key={l.itemId}
              log={l}
              cue={item?.cue}
              timer={timer?.itemId === l.itemId ? timer : null}
              onTimer={(set, seconds) =>
                setTimer(
                  set === null
                    ? null
                    : { itemId: l.itemId, set, endsAt: Date.now() + seconds * 1000 },
                )
              }
              onPatch={(set, patch) => patchSet(l, set, patch)}
            />
          )
        })}
      </ul>
      <div className="flex items-center gap-2">
        <button type="button" className={remaining ? outlineBtn : primaryBtn} onClick={finish}>
          <Check className="size-3.5" />
          {t('sessionPlan.finish')}
        </button>
        {remaining > 0 && !untouched && (
          <span className="text-[11px] text-muted-foreground">
            {t('sessionPlan.skippedNote', { count: remaining })}
          </span>
        )}
      </div>
    </div>
  )
}

function NumField({
  value,
  onCommit,
  label,
  step = 1,
  className,
}: {
  value: number | undefined
  onCommit: (n: number) => void
  label: string
  step?: number
  className?: string
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value))
  useEffect(() => setText(value === undefined ? '' : String(value)), [value])
  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      step={step}
      aria-label={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const n = Number(text)
        if (text !== '' && Number.isFinite(n) && n >= 0 && n !== value) onCommit(n)
        else setText(value === undefined ? '' : String(value))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      className={cn(input, 'h-7 w-14 px-1.5 text-center font-mono text-xs tabular-nums', className)}
    />
  )
}

function ItemRow({
  log: l,
  cue,
  timer,
  onTimer,
  onPatch,
}: {
  log: ItemLog
  cue?: string
  timer: { set: number; endsAt: number } | null
  onTimer: (set: number | null, seconds: number) => void
  onPatch: (set: number, patch: SetPatch) => void
}) {
  const { t, i18n } = useTranslation()
  const done = l.sets.every((s) => s.done)
  const now = useNow(!!timer, 250)
  const fired = useRef(false)
  useEffect(() => {
    fired.current = false
  }, [timer])
  useEffect(() => {
    if (!timer || fired.current || now < timer.endsAt) return
    fired.current = true
    playBellSound()
    onPatch(timer.set, { done: true, seconds: l.target.seconds })
    onTimer(null, 0)
  }, [now, timer, onPatch, onTimer, l.target.seconds])

  return (
    <li
      className={cn(
        'rounded-xl border border-border/60 px-3 py-2 transition-colors',
        done && 'border-primary/30 bg-primary/5',
      )}
    >
      <div className="flex items-start gap-2.5">
        <KindIcon kind={l.kind} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{l.name}</p>
          {cue && <p className="text-xs text-muted-foreground">{cue}</p>}
        </div>
      </div>
      <div className="mt-1.5 flex flex-col gap-1 pl-[26px]">
        {l.sets.map((s, i) => {
          const running = timer?.set === i
          const tick = () => onPatch(i, { done: !s.done })
          return (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                role="checkbox"
                aria-checked={s.done}
                aria-label={t('sessionPlan.setN', { n: i + 1 })}
                onClick={tick}
                className={cn(
                  'flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-colors',
                  s.done
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted-foreground/40 hover:border-primary',
                )}
              >
                {s.done && <Check className="size-3.5" strokeWidth={3} />}
              </button>
              <span className="w-10 text-[11px] text-muted-foreground">
                {t('sessionPlan.setN', { n: i + 1 })}
              </span>
              {l.kind === 'sets' && (
                <>
                  <NumField
                    value={s.reps}
                    label={t('sessionPlan.reps')}
                    onCommit={(reps) => onPatch(i, { reps })}
                  />
                  <span className="text-[11px] text-muted-foreground">{t('sessionPlan.reps')}</span>
                  {l.target.weight !== undefined && (
                    <>
                      <NumField
                        value={s.weight}
                        step={0.5}
                        label={t('sessionPlan.weight')}
                        onCommit={(weight) => onPatch(i, { weight })}
                      />
                      <span className="text-[11px] text-muted-foreground">{l.unit ?? 'kg'}</span>
                    </>
                  )}
                </>
              )}
              {l.kind === 'distance' && (
                <>
                  <NumField
                    value={s.distance}
                    step={0.1}
                    label={t('sessionPlan.distance')}
                    onCommit={(distance) => onPatch(i, { distance })}
                  />
                  <span className="text-[11px] text-muted-foreground">{l.unit ?? 'km'}</span>
                </>
              )}
              {l.kind === 'timed' &&
                (running ? (
                  <span className="flex items-center gap-1.5">
                    <TimerRing
                      ratio={1 - (timer.endsAt - now) / ((l.target.seconds ?? 1) * 1000)}
                    />
                    <span className="font-mono text-sm font-semibold tabular-nums" role="timer">
                      {clock(timer.endsAt - now)}
                    </span>
                    <button
                      type="button"
                      className={ghostBtn}
                      aria-label={t('sessionPlan.timerStop')}
                      onClick={() => onTimer(null, 0)}
                    >
                      <Square className="size-3" />
                    </button>
                  </span>
                ) : (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">
                      {duration(t, s.seconds ?? l.target.seconds ?? 0, i18n.language)}
                    </span>
                    {!s.done && (
                      <button
                        type="button"
                        className={cn(outlineBtn, 'py-1')}
                        onClick={() => onTimer(i, l.target.seconds ?? 60)}
                      >
                        <Timer className="size-3" />
                        {t('sessionPlan.timerStart')}
                      </button>
                    )}
                  </>
                ))}
            </div>
          )
        })}
      </div>
    </li>
  )
}

function Chips<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T | undefined
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
              value === o.value
                ? 'bg-foreground text-background'
                : 'bg-muted/60 text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function AskCoachForm({
  habitId,
  coachName,
  initial,
  onCancel,
  onPlanned,
}: {
  habitId: Id<'nodes'>
  coachName: string
  initial?: PlanProfile
  onCancel: () => void
  onPlanned: (sessions: PlanSession[], profile: PlanProfile) => void
}) {
  const { t, i18n } = useTranslation()
  const generate = useAction(api.sessionPlansNode.generate)
  const [level, setLevel] = useState(initial?.level ?? 'beginner')
  const [equipment, setEquipment] = useState(initial?.equipment ?? 'none')
  const [minutes, setMinutes] = useState(String(initial?.minutes ?? 30))
  const [busy, setBusy] = useState(false)

  const submit = () => {
    const m = Math.round(Number(minutes))
    const profile: PlanProfile = {
      level,
      equipment,
      ...(Number.isFinite(m) && m >= 5 && m <= 180 && { minutes: m }),
    }
    setBusy(true)
    generate({
      habitId,
      profile,
      locale: i18n.language,
      tzOffsetMin: new Date().getTimezoneOffset(),
    })
      .then((sessions) => onPlanned(sessions, profile))
      .catch((e) => {
        if (!handleUsageLimit(e)) toast.error(t('sessionPlan.planError'))
      })
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-border/60 p-3">
      <p className="text-xs text-muted-foreground">
        {t('sessionPlan.askIntro', { coach: coachName })}
      </p>
      <Chips
        label={t('sessionPlan.level')}
        value={level}
        onChange={setLevel}
        options={[
          { value: 'beginner', label: t('sessionPlan.levelBeginner') },
          { value: 'intermediate', label: t('sessionPlan.levelIntermediate') },
          { value: 'advanced', label: t('sessionPlan.levelAdvanced') },
        ]}
      />
      <Chips
        label={t('sessionPlan.equipment')}
        value={equipment}
        onChange={setEquipment}
        options={[
          { value: 'none', label: t('sessionPlan.equipNone') },
          { value: 'dumbbells', label: t('sessionPlan.equipDumbbells') },
          { value: 'gym', label: t('sessionPlan.equipGym') },
        ]}
      />
      <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {t('sessionPlan.minutes')}
        <input
          type="number"
          inputMode="numeric"
          min={5}
          max={180}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          className={cn(input, 'w-16 text-center')}
        />
      </label>
      <div className="flex gap-1.5">
        <button type="button" className={primaryBtn} disabled={busy} onClick={submit}>
          <Sparkles className={cn('size-3.5', busy && 'animate-pulse')} />
          {busy ? t('sessionPlan.planning', { coach: coachName }) : t('sessionPlan.planIt')}
        </button>
        <button type="button" className={ghostBtn} disabled={busy} onClick={onCancel}>
          {t('sessionPlan.cancel')}
        </button>
      </div>
    </div>
  )
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list
  const next = [...list]
  const [x] = next.splice(from, 1)
  next.splice(to, 0, x)
  return next
}

function IconBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  )
}

function PlanEditor({
  habitId,
  initial,
  profile,
  canRemove,
  onDone,
}: {
  habitId: Id<'nodes'>
  initial: PlanSession[]
  profile?: PlanProfile
  canRemove: boolean
  onDone: () => void
}) {
  const { t } = useTranslation()
  const save = useMutation(api.sessionPlans.savePlan)
  const remove = useMutation(api.sessionPlans.deletePlan)
  const [sessions, setSessions] = useState<PlanSession[]>(initial)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [busy, setBusy] = useState(false)

  const setSession = (si: number, patch: Partial<PlanSession>) =>
    setSessions((ss) => ss.map((s, i) => (i === si ? { ...s, ...patch } : s)))
  const setItem = (si: number, ii: number, patch: Partial<PlanItem>) =>
    setSessions((ss) =>
      ss.map((s, i) =>
        i === si ? { ...s, items: s.items.map((x, j) => (j === ii ? { ...x, ...patch } : x)) } : s,
      ),
    )
  const valid = sessions.some((s) => s.items.some((i) => i.name.trim()))

  const submit = () => {
    if (!valid) return void toast.error(t('sessionPlan.emptyEditor'))
    setBusy(true)
    save({ habitId, sessions, profile })
      .then(() => {
        toast.success(t('sessionPlan.saved'))
        onDone()
      })
      .catch(() => toast.error(t('sessionPlan.error')))
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex flex-col gap-3" data-testid="plan-editor">
      {sessions.map((s, si) => (
        <div key={s.id} className="flex flex-col gap-2 rounded-xl border border-border/60 p-2.5">
          <div className="flex items-center gap-1">
            <input
              value={s.name}
              maxLength={MAX_NAME}
              placeholder={t('sessionPlan.sessionPlaceholder')}
              aria-label={t('sessionPlan.sessionName')}
              onChange={(e) => setSession(si, { name: e.target.value })}
              className={cn(input, 'flex-1 font-semibold')}
            />
            {sessions.length > 1 && (
              <>
                <IconBtn
                  label={t('sessionPlan.moveUp')}
                  disabled={si === 0}
                  onClick={() => setSessions((ss) => move(ss, si, si - 1))}
                >
                  <ChevronUp className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.moveDown')}
                  disabled={si === sessions.length - 1}
                  onClick={() => setSessions((ss) => move(ss, si, si + 1))}
                >
                  <ChevronDown className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.deleteSession')}
                  onClick={() => setSessions((ss) => ss.filter((_, i) => i !== si))}
                >
                  <X className="size-4" />
                </IconBtn>
              </>
            )}
          </div>
          {s.items.map((it, ii) => (
            <div key={it.id} className="flex flex-col gap-1.5 rounded-lg bg-muted/30 p-2">
              <div className="flex items-center gap-1">
                <KindIcon kind={it.kind} />
                <input
                  value={it.name}
                  maxLength={MAX_NAME}
                  placeholder={t('sessionPlan.itemPlaceholder')}
                  aria-label={t('sessionPlan.itemName')}
                  onChange={(e) => setItem(si, ii, { name: e.target.value })}
                  className={cn(input, 'flex-1')}
                />
                <IconBtn
                  label={t('sessionPlan.moveUp')}
                  disabled={ii === 0}
                  onClick={() => setSession(si, { items: move(s.items, ii, ii - 1) })}
                >
                  <ChevronUp className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.moveDown')}
                  disabled={ii === s.items.length - 1}
                  onClick={() => setSession(si, { items: move(s.items, ii, ii + 1) })}
                >
                  <ChevronDown className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.delete')}
                  onClick={() => setSession(si, { items: s.items.filter((_, j) => j !== ii) })}
                >
                  <Trash2 className="size-3.5" />
                </IconBtn>
              </div>
              <ItemTargetEditor item={it} onChange={(p) => setItem(si, ii, p)} />
              <input
                value={it.cue ?? ''}
                maxLength={MAX_CUE}
                placeholder={t('sessionPlan.cuePlaceholder')}
                aria-label={t('sessionPlan.cue')}
                onChange={(e) => setItem(si, ii, { cue: e.target.value })}
                className={cn(input, 'text-xs')}
              />
            </div>
          ))}
          {s.items.length < MAX_ITEMS && (
            <button
              type="button"
              className={cn(ghostBtn, 'w-fit')}
              onClick={() => setSession(si, { items: [...s.items, blankItem()] })}
            >
              <Plus className="size-3.5" />
              {t('sessionPlan.addItem')}
            </button>
          )}
        </div>
      ))}
      {sessions.length < MAX_SESSIONS && (
        <button
          type="button"
          className={cn(ghostBtn, 'w-fit')}
          onClick={() => setSessions((ss) => [...ss, blankSession(t, ss.length)])}
        >
          <Plus className="size-3.5" />
          {t('sessionPlan.addSession')}
        </button>
      )}
      {sessions.length > 1 && (
        <p className="text-[11px] text-muted-foreground">{t('sessionPlan.rotationHint')}</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" className={primaryBtn} disabled={busy || !valid} onClick={submit}>
          {t('sessionPlan.save')}
        </button>
        <button type="button" className={ghostBtn} onClick={onDone}>
          {t('sessionPlan.cancel')}
        </button>
        {canRemove && (
          <button
            type="button"
            className={cn(ghostBtn, 'ml-auto', confirmRemove && 'text-destructive')}
            onClick={() => {
              if (!confirmRemove) return setConfirmRemove(true)
              void remove({ habitId })
                .then(onDone)
                .catch(() => toast.error(t('sessionPlan.error')))
            }}
          >
            <Trash2 className="size-3.5" />
            {confirmRemove ? t('sessionPlan.removeConfirm') : t('sessionPlan.remove')}
          </button>
        )}
      </div>
    </div>
  )
}

function ItemTargetEditor({
  item: it,
  onChange,
}: {
  item: PlanItem
  onChange: (p: Partial<PlanItem>) => void
}) {
  const { t } = useTranslation()
  const n = (v: string) => (v === '' ? undefined : Number(v))
  const field = (
    key: 'sets' | 'reps' | 'weight' | 'seconds' | 'distance',
    label: string,
    step = 1,
  ) => (
    <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
      {label}
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        value={it[key] ?? ''}
        onChange={(e) => onChange({ [key]: n(e.target.value) })}
        className={cn(input, 'h-7 w-14 px-1.5 text-center font-mono text-xs')}
      />
    </label>
  )
  const setKind = (kind: ItemKind) =>
    onChange(
      kind === 'sets'
        ? { kind, reps: it.reps ?? 10, seconds: undefined, distance: undefined, unit: undefined }
        : kind === 'timed'
          ? { kind, seconds: it.seconds ?? 60, reps: undefined, weight: undefined, unit: undefined }
          : {
              kind,
              sets: 1,
              distance: it.distance ?? 3,
              unit: 'km',
              reps: undefined,
              weight: undefined,
              seconds: undefined,
            },
    )
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
      <select
        value={it.kind}
        aria-label={t('sessionPlan.kind')}
        onChange={(e) => setKind(e.target.value as ItemKind)}
        className={cn(input, 'h-7 cursor-pointer text-xs')}
      >
        <option value="sets">{t('sessionPlan.kindSets')}</option>
        <option value="timed">{t('sessionPlan.kindTimed')}</option>
        <option value="distance">{t('sessionPlan.kindDistance')}</option>
      </select>
      {field('sets', t('sessionPlan.sets'))}
      {it.kind === 'sets' && (
        <>
          {field('reps', t('sessionPlan.reps'))}
          {field('weight', t('sessionPlan.weight'), 0.5)}
          <select
            value={it.unit === 'lb' ? 'lb' : 'kg'}
            aria-label={t('sessionPlan.unit')}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={cn(input, 'h-7 cursor-pointer text-xs')}
          >
            <option value="kg">kg</option>
            <option value="lb">lb</option>
          </select>
        </>
      )}
      {it.kind === 'timed' && field('seconds', t('sessionPlan.seconds'), 5)}
      {it.kind === 'distance' && (
        <>
          {field('distance', t('sessionPlan.distance'), 0.1)}
          <select
            value={it.unit ?? 'km'}
            aria-label={t('sessionPlan.unit')}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={cn(input, 'h-7 cursor-pointer text-xs')}
          >
            <option value="km">km</option>
            <option value="mi">mi</option>
            <option value="m">m</option>
          </select>
        </>
      )}
    </div>
  )
}
